import type { IWorkflowWidget, WidgetPayloadData, WidgetValidationError, GalleryMediaItem, ImageDimensions, SeedPayloadField } from '../interfaces/IWorkflowWidget';
import {
  getInvalidPricingAggregateMessage,
  type PricingAggregateMessageLanguage,
} from '../config/pricingAggregateMessages';
import {
  assignWidgetPayloadData,
  BILLING_PRICING_KEYS,
} from './pricingAggregateAssign';

const COST_BLOCKING_VALIDATION_FIELDS = new Set<string>([
  ...BILLING_PRICING_KEYS,
  'session_reference_media',
]);

export interface PayloadCollectionResult {
  payload: WidgetPayloadData;
  validationErrors: WidgetValidationError[];
  requiredFiles: File[];
  galleryUrls: GalleryMediaItem[];
  /** Client-only seed metadata used when cloning the payload for batch slots. */
  seedFields: SeedPayloadField[];
  hasRequiredWidgetErrors: boolean;
  /** True when estimating from the accepted partial payload could undercharge. */
  hasPricingErrors: boolean;
  /** Image dimensions collected from widgets (for pricing formula) */
  inputImageDimensions: ImageDimensions[];
  /** Known image dimensions split by whether Slots counters apply to them. */
  slotsInputImageDimensions: ImageDimensions[];
  standardInputImageDimensions: ImageDimensions[];
  /** Image files from widgets that do not provide pre-calculated dimensions. */
  unmeasuredSlotsImageFiles: File[];
  unmeasuredStandardImageFiles: File[];
  /**
   * Number of files in requiredFiles that have corresponding blob URLs in payload.
   * These files are placed FIRST in the requiredFiles array so that blob URL
   * replacement on the backend consumes the correct uploaded URLs.
   * Files without blob URLs (e.g. from BigInputImagesWidget) come after.
   */
  blobFileCount: number;
}

export interface CategorizedFilesPreview {
  images: File[];
  videos: File[];
  audios: File[];
}

export type SeedAdvanceCallback = () => void;

/**
 * Captures seed advancement while widget instances and their dispatched values
 * are still available. The returned callbacks remain usable after unmount.
 */
export function captureWidgetSeedAdvances(
  widgetRefs: Array<{ current: IWorkflowWidget | null }>
): SeedAdvanceCallback[] {
  return widgetRefs.flatMap((widgetRef) => {
    const advance = widgetRef.current?.captureSeedAdvance?.();
    return advance ? [advance] : [];
  });
}

/** Advances seed-capable widgets after their payloads have been dispatched. */
export function advanceWidgetSeeds(seedAdvances: SeedAdvanceCallback[]): void {
  seedAdvances.forEach((advance) => advance());
}

/**
 * Categorizes files by media type for preview purposes
 * This mirrors the server-side categorization logic
 */
export function categorizeFilesForPreview(files: File[]): CategorizedFilesPreview {
  const result: CategorizedFilesPreview = { images: [], videos: [], audios: [] };

  for (const file of files) {
    if (file.type.startsWith('image/')) {
      result.images.push(file);
    } else if (file.type.startsWith('video/')) {
      result.videos.push(file);
    } else if (file.type.startsWith('audio/')) {
      result.audios.push(file);
    }
  }

  return result;
}

/**
 * Collects payload data from all active widgets that implement IWorkflowWidget
 * @param widgetRefs - Array of React refs to widgets that implement IWorkflowWidget
 * @returns Combined payload data, validation errors, and files
 */
/**
 * Check whether a widget's payload data contains any blob: URLs.
 * Widgets that produce blob URLs in their data have a 1:1 correspondence
 * between those blobs and their requiredFiles. Widgets that don't (like
 * BigInputImagesWidget, which returns data:{}) contribute files that go
 * through standard media categorization only.
 */
function widgetDataHasBlobUrls(data: WidgetPayloadData): boolean {
  for (const value of Object.values(data)) {
    if (typeof value === 'string' && value.startsWith('blob:')) return true;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && item.startsWith('blob:')) return true;
      }
    }
  }
  return false;
}

export function collectWidgetPayloads(
  widgetRefs: React.RefObject<IWorkflowWidget>[],
  language: PricingAggregateMessageLanguage = 'en',
  inputFilesLogic: 'single_per_request' | 'all_per_request' = 'all_per_request',
): PayloadCollectionResult {
  const result: PayloadCollectionResult = {
    payload: {},
    validationErrors: [],
    requiredFiles: [],
    galleryUrls: [],
    seedFields: [],
    hasRequiredWidgetErrors: false,
    hasPricingErrors: false,
    inputImageDimensions: [],
    slotsInputImageDimensions: [],
    standardInputImageDimensions: [],
    unmeasuredSlotsImageFiles: [],
    unmeasuredStandardImageFiles: [],
    blobFileCount: 0
  };

  // Collect files in two buckets so blob-associated files come first
  const blobFiles: File[] = [];
  const standardFiles: File[] = [];
  const seedFieldsByPayloadKey = new Map<string, SeedPayloadField>();

  for (let i = 0; i < widgetRefs.length; i++) {
    const widgetRef = widgetRefs[i];
    if (!widgetRef.current) {
      continue;
    }

    const widget = widgetRef.current;

    // Validate widget state first
    const validationErrors = widget.validateState();
    result.validationErrors.push(...validationErrors);

    // If this is a required widget and has errors, mark it
    if (widget.isRequired() && validationErrors.length > 0) {
      result.hasRequiredWidgetErrors = true;
    }

    // Get payload data from widget
    const widgetPayload = widget.getPayloadData();

    // Merge widget data into combined payload. Pricing aggregates need
    // additive/max semantics across widgets instead of last-writer-wins.
    const rejectedAggregateKeys = assignWidgetPayloadData(result.payload, widgetPayload.data);
    const rejectedPricingKeys = rejectedAggregateKeys.filter(field =>
      COST_BLOCKING_VALIDATION_FIELDS.has(field)
    );
    if (rejectedPricingKeys.length > 0) {
      result.validationErrors.push(...rejectedPricingKeys.map(field => ({
        field,
        message: getInvalidPricingAggregateMessage(language),
      })));

      // Pricing data is a money-path invariant, independent of whether the UI
      // considers the contributing widget optional. Dropping an invalid value
      // and continuing would send the media without charging for it.
      result.hasRequiredWidgetErrors = true;
    }

    // Ordinary payload keys are last-writer-wins. Keep seed metadata aligned
    // with the widget that ultimately owns each emitted key.
    Object.keys(widgetPayload.data).forEach(key => seedFieldsByPayloadKey.delete(key));
    widgetPayload.seedFields?.forEach(seedField => {
      if (Object.prototype.hasOwnProperty.call(widgetPayload.data, seedField.payloadKey)) {
        seedFieldsByPayloadKey.set(seedField.payloadKey, seedField);
      }
    });

    // Separate files: blob-associated files first, standard files after.
    // This ensures replaceBlobUrlsWithUploaded on the backend consumes
    // the correct uploaded URLs (blob files map 1:1 to blob URLs in payload).
    if (widgetPayload.requiredFiles && widgetPayload.requiredFiles.length > 0) {
      if (widgetDataHasBlobUrls(widgetPayload.data)) {
        blobFiles.push(...widgetPayload.requiredFiles);
      } else {
        standardFiles.push(...widgetPayload.requiredFiles);
      }
    }

    // Collect any gallery URLs
    if (widgetPayload.galleryUrls) {
      result.galleryUrls.push(...widgetPayload.galleryUrls);
    }

    // Preserve whether image dimensions belong to Slots Input Media. Its
    // counters are narrowed per request, so pricing must not infer ownership
    // from widget/ref order.
    const dimensionSource = widgetPayload.inputImageDimensionsSource ?? 'standard';
    if (widgetPayload.inputImageDimensions?.length) {
      result.inputImageDimensions.push(...widgetPayload.inputImageDimensions);
      if (dimensionSource === 'slots') {
        result.slotsInputImageDimensions.push(...widgetPayload.inputImageDimensions);
      } else {
        result.standardInputImageDimensions.push(...widgetPayload.inputImageDimensions);
      }
    } else if (widgetPayload.requiredFiles?.length) {
      // BigInputImagesWidget intentionally exposes only files. Keep those
      // separate so the caller can extract their dimensions without losing
      // the source provenance of dimensions supplied by other widgets.
      const imageFiles = widgetPayload.requiredFiles.filter(file =>
        file.type.startsWith('image/')
      );
      if (dimensionSource === 'slots') {
        result.unmeasuredSlotsImageFiles.push(...imageFiles);
      } else {
        result.unmeasuredStandardImageFiles.push(...imageFiles);
      }
    }

    // Add any additional validation errors from getPayloadData
    if (widgetPayload.validationErrors) {
      result.validationErrors.push(...widgetPayload.validationErrors);
    }
  }

  // Blob-associated files first, then standard files (no blob URLs in payload)
  result.requiredFiles = [...blobFiles, ...standardFiles];
  result.blobFileCount = blobFiles.length;
  result.seedFields = [...seedFieldsByPayloadKey.values()];

  // Duration and video-dimension values describe the whole collected media
  // batch. `single_per_request` later narrows that batch to one file/gallery
  // item per request, but it cannot narrow these aggregates because widgets do
  // not expose per-item pricing provenance. Sending the batch aggregate on
  // every request would multiply duration charges and can select the wrong
  // resolution tier. Permit the one-media case, and fail closed for larger
  // batches until per-item aggregates are available.
  const mediaItemCount = result.requiredFiles.length + result.galleryUrls.length;
  if (inputFilesLogic === 'single_per_request' && mediaItemCount > 1) {
    const unsafeAggregateKeys = BILLING_PRICING_KEYS.filter(key =>
      Object.prototype.hasOwnProperty.call(result.payload, key)
    );
    if (unsafeAggregateKeys.length > 0) {
      result.validationErrors.push(...unsafeAggregateKeys.map(field => ({
        field,
        message: getInvalidPricingAggregateMessage(language),
      })));
      result.hasRequiredWidgetErrors = true;
    }
  }

  result.hasPricingErrors = result.validationErrors.some(error =>
    COST_BLOCKING_VALIDATION_FIELDS.has(error.field)
  );
  // Pricing validity is independent of whether the widget is configured as a
  // required input. Optional video/audio still incurs cost when present, so a
  // missing measurement must block the same launch gate as required-widget
  // validation instead of merely hiding the estimate.
  if (result.hasPricingErrors) {
    result.hasRequiredWidgetErrors = true;
  }

  return result;
}

/**
 * Gets action button configuration based on workflow action_button_type
 */
export function getActionButtonConfig(actionType: string, t: any) {
  const configs: Record<string, {
    text: string;
    icon: string;
    gradientFrom: string;
    gradientTo: string;
    hoverFrom: string;
    hoverTo: string;
  }> = {
    generate: {
      text: t.workflow?.actions?.generate || 'Generate',
      icon: 'IMAGE',
      gradientFrom: 'from-purple-600',
      gradientTo: 'to-blue-600',
      hoverFrom: 'hover:from-purple-700',
      hoverTo: 'hover:to-blue-700'
    },
    upscale: {
      text: t.workflow?.actions?.upscale || 'Upscale',
      icon: 'ZAPS', // Use ZAPS icon for upscale
        gradientFrom: 'from-purple-600',
        gradientTo: 'to-blue-600',
        hoverFrom: 'hover:from-purple-700',
        hoverTo: 'hover:to-blue-700'
    },
    process: {
      text: t.workflow?.actions?.process || 'Process',
      icon: 'SETTINGS',
        gradientFrom: 'from-purple-600',
        gradientTo: 'to-blue-600',
        hoverFrom: 'hover:from-purple-700',
        hoverTo: 'hover:to-blue-700'
    },
    create: {
      text: t.workflow?.actions?.create || 'Create',
      icon: 'PLUS',
        gradientFrom: 'from-purple-600',
        gradientTo: 'to-blue-600',
        hoverFrom: 'hover:from-purple-700',
        hoverTo: 'hover:to-blue-700'
    }
  };

  return configs[actionType] || configs.generate;
}
